import pako from "pako";
import { networkService } from "../components/config/axios";
import noImage from "../../assets/images/icon_upload.svg";
import { isAcceptAll } from "./cookieConsentUtils";

// Phone numbers are typically typed with formatting (spaces, dashes,
// parentheses) which a digits-only check would reject, misclassifying them
// as a URL and prepending https://. Require a minimum digit count so short
// numeric strings (e.g. an IP-like "192.168.1.1") aren't mistaken for one.
export const isPhoneLike = (raw) => {
  const trimmed = (raw || "").trim();
  if (!/^\+?[\d\s().-]+$/.test(trimmed)) return false;
  const digitCount = (trimmed.match(/\d/g) || []).length;
  return digitCount >= 7;
};

// Users sometimes paste a link copied straight from a mail client or phone app,
// prefix and all. Strip it so the rest of the pipeline sees the bare address/number.
export const stripLinkPrefix = (raw) => (raw || "").replace(/^(mailto|tel):/i, "");

export const normalizePhone = (raw) => {
  const trimmed = (raw || "").trim();
  const hasPlus = trimmed.startsWith("+");
  const digits = trimmed.replace(/\D/g, "");
  return (hasPlus ? "+" : "") + digits;
};

// Users routinely paste URLs that already carry a protocol, sometimes a broken
// one: bare "http://" (upgrade to https), a doubled prefix like
// "https://http://example.com" (from a pre-filled "https://" field), or a
// single-slash typo "https:/example.com". Repair all of these so the stored
// value is always one valid "https://" URL.
export const normalizeUrlProtocol = (raw) => {
  let val = (raw || "").trim();
  if (!val) return "";
  // Drop any run of leading protocol tokens sitting in front of a real one:
  // "https://http://x" -> "http://x", "https://https://x" -> "https://x".
  val = val.replace(/^(?:https?:\/{0,2})+(?=https?:\/\/)/i, "");
  // Repair single-slash typo: "https:/x" -> "https://x".
  val = val.replace(/^(https?):\/(?!\/)/i, "$1://");
  if (/^http:\/\//i.test(val)) return val.replace(/^http:\/\//i, "https://");
  if (/^https:\/\//i.test(val)) return val;
  return `https://${val}`;
};

export const isValidEmail = (raw) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((raw || "").trim());

export const isValidHttpUrl = (raw) => {
  const val = (raw || "").trim();
  if (!val || /^https?:\/\/$/i.test(val)) return false;
  try {
    const u = new URL(/^https?:\/\//i.test(val) ? val : `https://${val}`);
    return !!u.hostname && u.hostname.includes(".");
  } catch {
    return false;
  }
};

export const parseActionLink = (raw) => {
  const value = stripLinkPrefix((raw || "").trim());
  if (!value) return { url: null, mail: null, phone: null };
  if (value.includes("@")) return { url: null, mail: value, phone: null };
  if (isPhoneLike(value)) return { url: null, mail: null, phone: normalizePhone(value) };
  return { url: normalizeUrlProtocol(value), mail: null, phone: null };
};

export const compress = (obj) => {
  const jsonString = JSON.stringify(obj);
  const compressed = pako.deflate(jsonString, { to: "string" });
  return btoa(compressed);
};

export const decompress = (str) => {
  const compressed = atob(str);
  const jsonString = pako.inflate(compressed, { to: "string" });
  return JSON.parse(jsonString);
};

export const readSessionReceivers = () => {
  try {
    return JSON.parse(sessionStorage.getItem("receiver") || "{}") || {};
  } catch (error) {
    return {};
  }
};

export const getReceiverCookie = function () {
  return { ...readSessionReceivers(), ...getCookie("receiver", {}) };
};

export const setReceiverCookie = function (cookieKey, cookieValue) {
  if (isAcceptAll()) {
    var receiverCookie = getCookie("receiver", {});
    receiverCookie[cookieKey] = cookieValue;
    setCookie("receiver", receiverCookie);
    return;
  }
  const sessionReceivers = readSessionReceivers();
  sessionReceivers[cookieKey] = cookieValue;
  try {
    sessionStorage.setItem("receiver", JSON.stringify(sessionReceivers));
  } catch (error) {
    return;
  }
};

const readSessionOptin = () => {
  try {
    return JSON.parse(sessionStorage.getItem("optin") || "{}") || {};
  } catch (error) {
    return {};
  }
};

const writeSessionOptin = (value) => {
  try {
    sessionStorage.setItem("optin", JSON.stringify(value));
  } catch (error) {
    return;
  }
};

export const getOptinCookie = function () {
  return { ...readSessionOptin(), ...getCookie("optin", {}) };
};

export const setOptinCookie = function (token, value) {
  const persistent = getCookie("optin", {});
  const session = readSessionOptin();
  const hadPersistent = Object.prototype.hasOwnProperty.call(persistent, token);
  delete persistent[token];
  delete session[token];
  if (value !== undefined) {
    if (isAcceptAll()) persistent[token] = value;
    else session[token] = value;
  }
  if (hadPersistent || Object.prototype.hasOwnProperty.call(persistent, token)) setCookie("optin", persistent);
  writeSessionOptin(session);
};

// Durable (cross-tab) check for whether a recipient already opted in on this tracking
// link, backed by localStorage rather than sessionStorage so it survives new tabs/reopens.
export const isOptInAlreadyDone = function (token) {
  const receiverCookie = getReceiverCookie();
  const optinCookie = getOptinCookie();
  return (
    receiverCookie[token] !== undefined ||
    optinCookie[token]?.show === "none"
  );
};

export const setReceiverSession = async function (id, tlId, sessionId, receiverToken = null) {
  const query = receiverToken ? `?receiver_token=${encodeURIComponent(receiverToken)}` : "";
  const url = `/assign-session/${id}/${tlId}/${sessionId}.json${query}`;
  const response = await networkService(url, "GET");
  return response.data;
};

export const getCookie = function (storageKey, returnedValue) {
  var storageValue = localStorage.getItem(storageKey);
  if (storageValue === null || storageValue === undefined) {
    storageValue = returnedValue;
  } else {
    storageValue = JSON.parse(storageValue);
  }

  return storageValue;
};

export const setCookie = function (storageKey, storageValue) {
  var compressedValue = JSON.stringify(storageValue);
  localStorage.setItem(storageKey, compressedValue);
};

export const dispatchEvent = (name, id) => {
  const event = new Event(name);
  const emitter = document.getElementById(id);
  emitter.dispatchEvent(event);
};

export const scrollPage = (number, behavior) => {
  const allCanvasElements = document.getElementsByClassName("react-pdf__Page");
  const canvasArray = Array.from(allCanvasElements);
  const targetCanvas = canvasArray.find(
    (canvas) => canvas.getAttribute("data-page-number") === `${number}`
  );
  if (targetCanvas) {
    targetCanvas.scrollIntoView({ behavior: behavior });
  }
};

export const handleClearCookie = async () => {
  networkService("users/sign_out", "delete")
    .then((response) => {
      localStorage.clear();
      window.location.href = "/";
    })
    .catch((error) => {
      console.error("Error during sign out:", error);
    });
};

const toHHMMSS = (time) => {
  var sec_num = parseInt(time, 10); 
  var hours = Math.floor(sec_num / 3600);
  var minutes = Math.floor((sec_num - hours * 3600) / 60);
  var seconds = sec_num - hours * 3600 - minutes * 60;
  hours < 1 ? (hours = "--") : hours;
  if (minutes < 10) {
    minutes = "0" + minutes;
  }
  if (seconds < 10) {
    seconds = "0" + seconds;
  }
  return (hours < 10 ? "0" + hours : hours) + ":" + minutes + ":" + seconds;
};

export const filterCommonPage = (pages) => {
  let isLandingPage = pages?.findIndex(
    (item) => item?.page == item?.landing_page
  );
  var filteredPages = [];
  for (let i = 0; i < pages.length; i++) {
    if (pages.length > i + 1) {
      if (pages[i].page === pages[i + 1].page) {
        pages[i] = {
          date: pages[i].date,
          date_wrt_time_zone: pages[i].date_wrt_time_zone,
          element_tags: pages[i].element_tags.concat(pages[i + 1].element_tags),
          elements_imp_clicks: pages[i].elements_imp_clicks.concat(
            pages[i + 1].elements_imp_clicks
          ),
          exit_page: pages[i].exit_page,
          exit_page_time: pages[i].exit_page_time,
          is_landing_page: i == isLandingPage,
          landing_page: pages[i].landing_page,
          language_editions: pages[i].language_editions.concat(
            pages[i + 1].language_editions
          ),
          min_viewed: toHHMMSS(pages[i].seconds + pages[i + 1].seconds),
          optin_done: pages[i].optin_done,
          optin_reject: pages[i].optin_reject,
          once_optin: pages[i].once_optin,
          other_clicks: pages[i].other_clicks.concat(pages[i + 1].other_clicks),
          resumes: [...new Map((pages[i].resumes || []).concat(pages[i + 1].resumes || []).map(r => [r.resume_at, r])).values()],
          restarts: [...new Map((pages[i].restarts || []).concat(pages[i + 1].restarts || []).map(r => [r.restart_at, r])).values()],
          cookie_consents: [...new Map((pages[i].cookie_consents || []).concat(pages[i + 1].cookie_consents || []).map(c => [c.consented_at_ts, c])).values()],
          rri_answers: (pages[i].rri_answers || []).concat(pages[i + 1].rri_answers || []),
          page: pages[i].page,
          seconds: pages[i].seconds + pages[i + 1].seconds,
          session_pause_time: pages[i].session_pause_time,
          show_pause_time: pages[i].show_pause_time,
          time_tooltip: pages[i].time_tooltip,
          video_on_page: pages[i].video_on_page,
          viewed_at: pages[i].viewed_at,
        };
        filteredPages.push(pages[i]);
        i++;
      } else {
        filteredPages.push({
          ...pages[i],
          is_landing_page: i == isLandingPage,
        });
      }
    } else {
      filteredPages.push({
        ...pages[pages.length - 1],
        is_landing_page: i == isLandingPage,
      });
    }
  }
  if (filteredPages.length > 2) {
    if (
      filteredPages[2].optin_done &&
      filteredPages[2].page === filteredPages[2].landing_page &&
      filteredPages[2].seconds < 3
    ) {
      filteredPages.splice(2, 1);
    }
  }

  return filteredPages;
};

export const handleErrorImage = (element) => {
  element.target.src = noImage;
};

export const viewsPercentage = (score, total) =>
  ((score / total) * 100).toFixed(2);

export const normalizeDimensions = (val, currentScale, desiredScale) => {
  if (undefined === desiredScale) {
    desiredScale = window.objInit?.zoom_lvl || 1;
  }
  return Math.round(val * (desiredScale / currentScale));
};

// Precise normalization that preserves enough decimal places to avoid
// round-trip drift (screen→base→screen). Unlike normalizeDimensions which
// uses Math.round() to an integer, this keeps 4 decimal places so the
// reverse conversion (base * zoom → screen) lands on the original pixel.
export const roundTripSafeNormalize = (screenValue, currentZoom, desiredZoom = 1) => {
  const result = screenValue * (desiredZoom / currentZoom);
  return Math.round(result * 10000) / 10000;
};

export const VALUE_FIELD_UI_DEFAULTS = {
  bg_color: 'rgba(255, 255, 255, 1)',
  animation: '',
  frequency: '',
  disappear: '',
};

const parseColorToRgba = (value) => {
  if (typeof value !== 'string') return null;
  const v = value.trim();
  if (v.toLowerCase() === 'transparent') return { r: 0, g: 0, b: 0, a: 0 };

  const rgbaMatch = v.match(/^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*(?:,\s*(0|1|0?\.\d+))?\s*\)$/i);
  if (rgbaMatch) {
    return {
      r: parseInt(rgbaMatch[1], 10),
      g: parseInt(rgbaMatch[2], 10),
      b: parseInt(rgbaMatch[3], 10),
      a: rgbaMatch[4] === undefined ? 1 : parseFloat(rgbaMatch[4]),
    };
  }

  const hexMatch = v.match(/^#([0-9a-f]{3}|[0-9a-f]{4}|[0-9a-f]{6}|[0-9a-f]{8})$/i);
  if (hexMatch) {
    let h = hexMatch[1];
    if (h.length === 3 || h.length === 4) h = h.split('').map((c) => c + c).join('');
    return {
      r: parseInt(h.slice(0, 2), 16),
      g: parseInt(h.slice(2, 4), 16),
      b: parseInt(h.slice(4, 6), 16),
      a: h.length === 8 ? parseInt(h.slice(6, 8), 16) / 255 : 1,
    };
  }

  return null;
};

const colorsMatch = (a, b) => {
  if (!a || !b) return false;
  return a.r === b.r && a.g === b.g && a.b === b.b && Math.round(a.a * 100) === Math.round(b.a * 100);
};

export const getComparableTool = (toolObj) => {
  if (!toolObj || typeof toolObj !== 'object') return toolObj;
  const { _baseGeometry, ...comparable } = toolObj;

  if (comparable.value && typeof comparable.value === 'object') {
    const value = { ...comparable.value };
    Object.entries(VALUE_FIELD_UI_DEFAULTS).forEach(([key, defaultValue]) => {
      const fieldValue = value[key];
      if (fieldValue === undefined || fieldValue === null || fieldValue === '') {
        delete value[key];
        return;
      }

      if (key === 'bg_color') {
        const parsed = parseColorToRgba(fieldValue);
        if (colorsMatch(parsed, parseColorToRgba(defaultValue))) {
          delete value[key];
        } else if (parsed) {
          value[key] = `${parsed.r},${parsed.g},${parsed.b},${Math.round(parsed.a * 100) / 100}`;
        }
        return;
      }

      if (fieldValue === defaultValue) delete value[key];
    });
    comparable.value = value;
  }
  return comparable;
};

export const getNormalizedDimensions = (
  geometry = {},
  zoom = 1,
  pageDimensions = {},
  element,
  canvas
) => {
  const page_w = pageDimensions.width || 800;
  const page_h = pageDimensions.height || 1000;

  const rendered_w = canvas?.offsetWidth || page_w;
  const rendered_h = canvas?.offsetHeight || page_h;

  const scaleX = typeof zoom === "object" ? zoom.value || 1 : zoom;
  const scaleY = scaleX;

  let top = Math.round((geometry.top ?? 0) * scaleY);
  let left = Math.round((geometry.left ?? 0) * scaleX);
  let height = Math.round((geometry.height ?? 0) * scaleY);
  let width = Math.round((geometry.width ?? 0) * scaleX);

  if (geometry.keep_within_page) {
    top = Math.max(0, Math.min(top, rendered_h - height));
    left = Math.max(0, Math.min(left, rendered_w - width));
  }

  const geo = {
    position: "absolute",
    top,
    left,
    width,
    height,
    fontSize: `${10 * scaleY}pt`,
    lineHeight: 1.3,
  };

  return {
    top,
    left,
    height,
    width,
    geo,
  };
};

export const getOffset = (element) => {
  if (!element) return { top: 0, left: 0 };
  const rect = element.getBoundingClientRect();
  return { top: rect.top + window.scrollY, left: rect.left + window.scrollX };
};

export const calculateRectPos = (x1, y1, x2, y2) => {
  const left = Math.min(x1, x2);
  const top = Math.min(y1, y2);
  const width = Math.abs(x1 - x2);
  const height = Math.abs(y1 - y2);
  return { left, top, width, height };
};

export const gifProportion = (maxWidth, maxHeight, gifWidth, gifHeight) => {
  if (gifWidth === 0 || gifHeight === 0) {
    return [0, 0];
  }

  var widthRatio = maxWidth / gifWidth;
  var heightRatio = maxHeight / gifHeight;
  let [finalWidth,finalHeight]= fitImageIntoDrawnBox(maxWidth,maxHeight,gifWidth,gifHeight)

  // Pick the larger ratio to ensure one side fills its max
  var scale = Math.max(widthRatio, heightRatio);

  var newWidth = Math.round(gifWidth * scale);
  var newHeight = Math.round(gifHeight * scale);

  // return [newWidth, newHeight];
  return [finalWidth,finalHeight]
};

export const gifProportionCover = (maxWidth, maxHeight, gifWidth, gifHeight) => {
  if (gifWidth === 0 || gifHeight === 0) return [0, 0];

  const widthRatio = maxWidth / gifWidth;
  const heightRatio = maxHeight / gifHeight;

  const scale = Math.max(widthRatio, heightRatio); // fill box

  const newWidth = Math.round(gifWidth * scale);
  const newHeight = Math.round(gifHeight * scale);

  return [newWidth, newHeight];
};


export const constProportionRespectUserDrawnBox = (drawnWidth, drawnHeight, imageWidth, imageHeight) => {
  if (!imageWidth || !imageHeight) return [0, 0];

  const imageAspect = imageWidth / imageHeight;
  let finalWidth, finalHeight;

  if (drawnWidth >= drawnHeight) {
    finalWidth  = drawnWidth;
    finalHeight = Math.round(drawnWidth / imageAspect);
  } else {
    finalHeight = drawnHeight;
    finalWidth  = Math.round(drawnHeight * imageAspect);
  }

  return [finalWidth, finalHeight];
};

export const applyProportionalResize = (changedKey, newValue, originalWidth, originalHeight) => {
  if (!originalWidth || !originalHeight || !newValue) return [originalWidth, originalHeight];

  const aspectRatio = originalWidth / originalHeight;

  if (changedKey === "width") {
    const width = newValue;
    const height = Math.round(width / aspectRatio);
    return [width, height];
  } else {
    const height = newValue;
    const width = Math.round(height * aspectRatio);
    return [width, height];
  }
};

export const fitImageIntoDrawnBox = (drawnWidth, drawnHeight, imageWidth, imageHeight) => {
  if (!imageWidth || !imageHeight || !drawnWidth || !drawnHeight) return [0, 0];

  const imageAspect = imageWidth / imageHeight;
  const imageOrientation = imageWidth >= imageHeight ? "horizontal" : "vertical";
  const boxOrientation = drawnWidth >= drawnHeight ? "horizontal" : "vertical";

  let finalWidth, finalHeight;

  if (imageOrientation === boxOrientation) {
    // --- Same orientation ---
    if (imageOrientation === "horizontal") {
      // Match box width and adjust height
      finalWidth = drawnWidth;
      finalHeight = Math.round(drawnWidth / imageAspect);
    } else {
      // Vertical → match box height and adjust width
      finalHeight = drawnHeight;
      finalWidth = Math.round(drawnHeight * imageAspect);
    }
  } else {
    // --- Different orientation → contain logic ---
    const boxAspect = drawnWidth / drawnHeight;
    if (imageAspect > boxAspect) {
      finalWidth = drawnWidth;
      finalHeight = Math.round(drawnWidth / imageAspect);
    } else {
      finalHeight = drawnHeight;
      finalWidth = Math.round(drawnHeight * imageAspect);
    }
  }

  return [finalWidth, finalHeight];
};



          